import React, { useState, useMemo, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import SafeAnimatePresence from "../../../components/ui/SafeAnimatePresence";
import { FiPhone, FiShoppingBag, FiSearch, FiChevronUp, FiChevronDown, FiTrash2, FiMapPin, FiRefreshCw, FiX, FiUserPlus, FiCheck } from "react-icons/fi";
import type { PhoneNumber, VoiceAgent } from "../voice.types";
import { getPhoneNumbersDF, searchAvailableNumbers, purchasePhoneNumber, releasePhoneNumber, assignAgentToNumber, type DfAvailableNumber } from "../../../services/voiceDataFabricService";
import { mapDfPhone } from "../voice.data";
import "../../../styles/Voice.css";
import "../../../styles/MomentumCommand.css";

/* ── types ── */
type SubTab = "my" | "buy";
type SortKey = "number" | "city" | "region";
type SortDir = "asc" | "desc";

interface PhoneNumbersTabProps {
  phones: PhoneNumber[];
  agents: VoiceAgent[];
  onPurchased?: () => void;
  subTab?: "my" | "buy";
  onSubTabChange?: (t: "my" | "buy") => void;
  mySearch?: string;
  buySearch?: string;
}

/* ── helpers ── */
function capString(c: DfAvailableNumber["capabilities"]): string {
  if (!c) return "—";
  const parts: string[] = [];
  if (c.MMS || c.mms) parts.push("MMS");
  if (c.SMS || c.sms) parts.push("SMS");
  if (c.voice) parts.push("Voice");
  return parts.join(", ") || "—";
}

function getNum(n: DfAvailableNumber): string {
  return n.phone_number ?? n.phoneNumber ?? "";
}
function getCity(n: DfAvailableNumber): string {
  return n.locality ?? "";
}
function getRegion(n: DfAvailableNumber): string {
  return n.region ?? "";
}

function sortNumbers(list: DfAvailableNumber[], key: SortKey, dir: SortDir): DfAvailableNumber[] {
  return [...list].sort((a, b) => {
    let av = "", bv = "";
    if (key === "number") { av = getNum(a); bv = getNum(b); }
    else if (key === "city") { av = getCity(a); bv = getCity(b); }
    else { av = getRegion(a); bv = getRegion(b); }
    return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });
}

/* ── Purchase Modal ── */
interface PurchaseModalProps {
  number: DfAvailableNumber;
  onClose: () => void;
  onConfirm: (phoneNumber: string, label: string) => Promise<void>;
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({ number, onClose, onConfirm }) => {
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const num = getNum(number);
  const displayNum = number.friendly_name ?? number.friendlyName ?? num;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await onConfirm(num, label);
      onClose();
    } catch (e: any) {
      setError(e.message || "Purchase failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pn-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        className="pn-modal"
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ duration: 0.18 }}
      >
        {/* Header */}
        <div className="pn-modal-header">
          <div className="pn-modal-title">
            <FiShoppingBag size={16} />
            Purchase Number
          </div>
          <button className="pn-modal-close" onClick={onClose}>
            <FiX size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="pn-modal-body">
          <div className="pn-modal-field">
            <label className="pn-modal-label">NUMBER TO PURCHASE</label>
            <div className="pn-modal-number">{displayNum}</div>
          </div>

          <div className="pn-modal-field">
            <label className="pn-modal-label">LABEL FOR THIS NUMBER</label>
            <input
              className="pn-modal-input"
              placeholder="e.g., Main Sales Line"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) handleConfirm(); }}
            />
          </div>

          {error && (
            <div className="pn-toast pn-toast--err">{error}</div>
          )}
        </div>

        {/* Footer */}
        <div className="pn-modal-footer">
          <button className="pn-modal-cancel" onClick={onClose} disabled={loading}>
            <FiX size={13} /> Cancel
          </button>
          <button className="pn-modal-confirm" onClick={handleConfirm} disabled={loading}>
            {loading
              ? <FiRefreshCw size={13} className="pn-spin" />
              : <FiShoppingBag size={13} />}
            {loading ? "Purchasing..." : "Confirm Purchase"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Assign Agent Modal ── */
interface AssignModalProps {
  phone: PhoneNumber;
  agents: VoiceAgent[];
  onClose: () => void;
  onConfirm: (phoneNumberId: string, agentId: string, agentName: string) => Promise<void>;
}

const AssignModal: React.FC<AssignModalProps> = ({ phone, agents, onClose, onConfirm }) => {
  const [selectedAgentId, setSelectedAgentId] = useState(phone.assignedAgentId ?? "");
  const [selectedAgentName, setSelectedAgentName] = useState(phone.assignedAgentName ?? "");
  const [agentSearch, setAgentSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filteredAgents = agents.filter((a) =>
    a.name.toLowerCase().includes(agentSearch.toLowerCase())
  );

  const handleSelect = (agent: VoiceAgent) => {
    setSelectedAgentId(agent.elevenLabsAgentId);
    setSelectedAgentName(agent.name);
    setAgentSearch("");
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedAgentId("");
    setSelectedAgentName("");
    setIsOpen(false);
  };

  const handleConfirm = async () => {
    if (!selectedAgentId) return;
    setLoading(true);
    setError(null);
    try {
      await onConfirm(phone.phoneNumberId, selectedAgentId, selectedAgentName);
      onClose();
    } catch (e: any) {
      setError(e.message || "Failed to assign agent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pn-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        className="pn-modal"
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }}
        transition={{ duration: 0.18 }}
      >
        <div className="pn-modal-header">
          <div className="pn-modal-title"><FiUserPlus size={16} /> Assign Agent</div>
          <button className="pn-modal-close" onClick={onClose}><FiX size={16} /></button>
        </div>
        <div className="pn-modal-body">
          <div className="pn-modal-field">
            <label className="pn-modal-label">PHONE NUMBER</label>
            <div className="pn-modal-number">{phone.phoneNumber}</div>
          </div>
          <div className="pn-modal-field">
            <label className="pn-modal-label">SELECT AGENT</label>
            <div className="pn-agent-select">

              {/* Trigger button */}
              <div
                className="pn-assign-trigger"
                onClick={() => setIsOpen((v) => !v)}
              >
                <span className={selectedAgentName ? "pn-assign-trigger-value" : "pn-assign-trigger-placeholder"}>
                  {selectedAgentName || "Select an agent..."}
                </span>
                <span className="pn-assign-trigger-right">
                  {selectedAgentName && (
                    <button className="pn-agent-clear" onClick={handleClear}><FiX size={12} /></button>
                  )}
                  <FiChevronDown
                    size={15}
                    className="pn-assign-trigger-chevron"
                    style={{ transform: isOpen ? "rotate(180deg)" : "none" }}
                  />
                </span>
              </div>

              {/* Expanding panel */}
              <SafeAnimatePresence>
                {isOpen && (
                  <motion.div
                    className="pn-assign-panel"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {/* Search */}
                    <div className="pn-assign-search">
                      <FiSearch size={13} className="pn-assign-search-icon" />
                      <input
                        autoFocus
                        className="pn-assign-search-input"
                        placeholder="Search agents..."
                        value={agentSearch}
                        onChange={(e) => setAgentSearch(e.target.value)}
                      />
                    </div>

                    {/* List */}
                    <div className="pn-assign-list">
                      {filteredAgents.length === 0 ? (
                        <div className="pn-assign-empty">No agents found</div>
                      ) : filteredAgents.map((a) => (
                        <div
                          key={a.elevenLabsAgentId || a.id}
                          className={`pn-assign-option ${selectedAgentId === a.elevenLabsAgentId ? "pn-assign-option--active" : ""}`}
                          onClick={() => handleSelect(a)}
                        >
                          <div className="pn-assign-option-avatar">
                            {a.name.slice(0, 1).toUpperCase()}
                          </div>
                          <span>{a.name}</span>
                          {selectedAgentId === a.elevenLabsAgentId && (
                            <FiCheck size={14} className="pn-assign-check-icon" />
                          )}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </SafeAnimatePresence>

            </div>
          </div>
          {error && <div className="pn-toast pn-toast--err">{error}</div>}
        </div>
        <div className="pn-modal-footer">
          <button className="pn-modal-cancel" onClick={onClose} disabled={loading}>
            <FiX size={13} /> Cancel
          </button>
          <button className="pn-modal-confirm" onClick={handleConfirm} disabled={loading || !selectedAgentId}>
            {loading ? <FiRefreshCw size={13} className="pn-spin" /> : <FiUserPlus size={13} />}
            {loading ? "Assigning..." : "Assign"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* ── Delete Confirm Modal ── */
interface DeleteModalProps {
  phone: PhoneNumber;
  onClose: () => void;
  onConfirm: (twilioSid: string, phoneNumberId: string) => Promise<void>;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ phone, onClose, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      await onConfirm(phone.twilioSid, phone.phoneNumberId);
      onClose();
    } catch (e: any) {
      const raw = e.message || "";
      // Try to extract a clean message from Twilio JSON error strings
      let msg = "Failed to release number. Please try again.";
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.details) {
          const inner = JSON.parse(parsed.details);
          msg = inner?.message ?? parsed.details;
        } else if (parsed?.message) {
          msg = parsed.message;
        }
      } catch {
        if (raw) msg = raw.length > 120 ? "Failed to release number. Please try again." : raw;
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.div
        className="feedback-popup__backdrop pn-delete-modal__backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={onClose}
      />
      <motion.div
        className="feedback-popup glass-elevated pn-delete-modal__popup"
        style={{ x: "-50%", y: "-50%" }}
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.92 }}
        transition={{ duration: 0.2, ease: [0.34, 1.56, 0.64, 1] }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="feedback-popup__header">
          <h3 className="feedback-popup__title">🗑️ Release Number?</h3>
        </div>

        <div className="feedback-popup__content pn-delete-modal__content">
          <p className="pn-delete-modal__text">
            Are you sure you want to release the number{" "}
            <br />
            <strong className="pn-delete-modal__number">{phone.phoneNumber}</strong> ?
          </p>
          <p className="pn-delete-modal__text pn-delete-modal__text--last">
            This action cannot be undone.
          </p>
          {error && <div className="pn-toast pn-toast--err pn-toast--spaced">{error}</div>}
        </div>

        <div className="feedback-popup__actions">
          <motion.button
            className="feedback-popup__btn feedback-popup__btn--cancel"
            onClick={onClose}
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiX size={13} /> Cancel
          </motion.button>
          <motion.button
            className="feedback-popup__btn feedback-popup__btn--danger"
            onClick={handleConfirm}
            disabled={loading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {loading ? <FiRefreshCw size={13} className="pn-spin" /> : <FiCheck size={13} />}
            {loading ? "Releasing..." : "Yes, Release"}
          </motion.button>
        </div>
      </motion.div>
    </>
  );
};

/* ── Main Component ── */
const PhoneNumbersTab: React.FC<PhoneNumbersTabProps> = ({
  phones: phonesProp,
  agents,
  onPurchased,
  subTab: subTabProp,
  onSubTabChange,
  mySearch: mySearchProp,
  buySearch: buySearchProp,
}) => {
  const [subTabInternal, setSubTabInternal] = useState<SubTab>("my");
  const subTab = subTabProp ?? subTabInternal;
  const setSubTab = (t: SubTab) => { setSubTabInternal(t); onSubTabChange?.(t); };

  /* ── My Numbers ── */
  const [phones, setPhones] = useState<PhoneNumber[]>(phonesProp);
  const [myLoading, setMyLoading] = useState(false);
  const [myError, setMyError] = useState<string | null>(null);
  const [mySearchInternal, setMySearchInternal] = useState("");
  const mySearch = mySearchProp ?? mySearchInternal;

  const fetchPhones = useCallback(async () => {
    setMyLoading(true);
    setMyError(null);
    try {
      const data = await getPhoneNumbersDF();
      setPhones(data.map(mapDfPhone));
    } catch (e: any) {
      setMyError(e.message || "Failed to load phone numbers");
    } finally {
      setMyLoading(false);
    }
  }, []);

  // Sync from parent prop on initial load only; user-triggered refresh uses fetchPhones()
  useEffect(() => {
    if (phones.length === 0 && phonesProp.length > 0) {
      setPhones(phonesProp);
    }
  }, [phonesProp]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Buy Numbers ── */
  const [buySearchInternal, setBuySearchInternal] = useState("");
  const buySearch = buySearchProp ?? buySearchInternal;
  const [available, setAvailable] = useState<DfAvailableNumber[]>([]);
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("number");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  /* ── Purchase Modal ── */
  const [purchaseTarget, setPurchaseTarget] = useState<DfAvailableNumber | null>(null);

  /* ── Assign Modal ── */
  const [assignTarget, setAssignTarget] = useState<PhoneNumber | null>(null);

  const handleAssignConfirm = async (phoneNumberId: string, agentId: string, agentName: string) => {
    await assignAgentToNumber(phoneNumberId, agentId);
    setPhones((prev) =>
      prev.map((p) =>
        p.phoneNumberId === phoneNumberId
          ? { ...p, assignedAgentId: agentId, assignedAgentName: agentName }
          : p
      )
    );
  };

  /* ── Delete Modal ── */
  const [deleteTarget, setDeleteTarget] = useState<PhoneNumber | null>(null);

  const handleDeleteConfirm = async (twilioSid: string, phoneNumberId: string) => {
    await releasePhoneNumber(twilioSid);
    setPhones((prev) => prev.filter((p) => p.phoneNumberId !== phoneNumberId));
    onPurchased?.();
  };

  const fetchAvailable = useCallback(async (query: string) => {
    setBuyLoading(true);
    setBuyError(null);
    try {
      const trimmed = query.trim();
      const params: { areaCode?: string; contains?: string } = {};
      if (/^\d{3}$/.test(trimmed)) params.areaCode = trimmed;
      else if (trimmed) params.contains = trimmed;
      const results = await searchAvailableNumbers(params);
      setAvailable(results);
    } catch (e: any) {
      setBuyError(e.message || "Failed to load available numbers");
      setAvailable([]);
    } finally {
      setBuyLoading(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === "buy" && available.length === 0 && !buyLoading) fetchAvailable("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  const handlePurchaseConfirm = async (phoneNumber: string, label: string) => {
    await purchasePhoneNumber(phoneNumber, label);
    onPurchased?.();
    // Remove purchased number from available list
    setAvailable((prev) => prev.filter((n) => getNum(n) !== phoneNumber));
  };

  const sortedAvailable = useMemo(() => {
    const filtered = available.filter((n) => {
      const q = buySearch.toLowerCase().trim();
      if (!q) return true;
      return (
        getNum(n).toLowerCase().includes(q) ||
        getCity(n).toLowerCase().includes(q) ||
        getRegion(n).toLowerCase().includes(q)
      );
    });
    return sortNumbers(filtered, sortKey, sortDir);
  }, [available, buySearch, sortKey, sortDir]);

  const filteredPhones = useMemo(() => {
    const q = mySearch.toLowerCase();
    if (!q) return phones;
    return phones.filter(
      (p) =>
        p.phoneNumber.toLowerCase().includes(q) ||
        (p.label ?? "").toLowerCase().includes(q) ||
        (p.assignedAgentName ?? "").toLowerCase().includes(q),
    );
  }, [phones, mySearch]);

  const SortHeader = ({ label, sk }: { label: string; sk: SortKey }) => (
    <span
      className="pn-sort-header"
      onClick={() => {
        if (sortKey === sk) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(sk); setSortDir("asc"); }
      }}
    >
      {label}
      <span className="pn-sort-icons">
        <FiChevronUp size={10} className={sortKey === sk && sortDir === "asc" ? "pn-sort-active" : ""} />
        <FiChevronDown size={10} className={sortKey === sk && sortDir === "desc" ? "pn-sort-active" : ""} />
      </span>
    </span>
  );

  return (
    <>
      <motion.div
        key="phones"
        className="voice-tab-content"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 8 }}
        transition={{ duration: 0.18 }}
      >

        {/* ══ MY PHONE NUMBERS ══ */}
        {subTab === "my" && (
          <motion.div key="my" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {myError && <div className="pn-toast pn-toast--err pn-toast--gap">{myError}</div>}

            {myLoading ? (
              <div className="pn-table">
                <div className="pn-table-head pn-table-head--my">
                  <span>Label</span><span>Number</span><span>Assigned Agent</span><span>Status</span><span>Actions</span>
                </div>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="pn-table-row pn-table-row--my">
                    <span className="pn-cell pn-cell--label"><div className="skeleton skeleton-text" style={{ width: '70%', height: 11 }} /></span>
                    <span className="pn-cell pn-cell--number"><div className="skeleton skeleton-text" style={{ width: '80%', height: 11 }} /></span>
                    <span className="pn-cell"><div className="skeleton skeleton-rect" style={{ width: 100, height: 22, borderRadius: 20 }} /></span>
                    <span className="pn-cell"><div className="skeleton skeleton-rect" style={{ width: 64, height: 22, borderRadius: 20 }} /></span>
                    <span className="pn-cell pn-cell--actions" style={{ display: 'flex', gap: 8 }}>
                      <div className="skeleton skeleton-rect" style={{ width: 24, height: 24, borderRadius: 6 }} />
                      <div className="skeleton skeleton-rect" style={{ width: 24, height: 24, borderRadius: 6 }} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
            <div className="pn-table">
              <div className="pn-table-head pn-table-head--my">
                <span>Label</span>
                <span>Number</span>
                <span>Assigned Agent</span>
                <span>Status</span>
                <span>Actions</span>
              </div>
              {filteredPhones.length === 0 ? (
                <div className="pn-empty">No phone numbers found.</div>
              ) : (
                filteredPhones.map((p) => (
                  <div key={p.id} className="pn-table-row pn-table-row--my">
                    <span className="pn-cell pn-cell--label">{p.label || <span className="pn-muted">—</span>}</span>
                    <span className="pn-cell pn-cell--number">{p.phoneNumber}</span>
                    <span className="pn-cell">
                      {p.assignedAgentName
                        ? <span className="pn-agent-pill">{p.assignedAgentName}</span>
                        : <span className="pn-muted">Unassigned</span>}
                    </span>
                    <span className="pn-cell">
                      <span className={`pn-status-pill pn-status-pill--${p.assignedAgentId ? "active" : "unassigned"}`}>
                        <span className="pn-status-dot" />
                        {p.assignedAgentId ? "Active" : "Unassigned"}
                      </span>
                    </span>
                    <span className="pn-cell pn-cell--actions">
                      <button className="pn-icon-btn pn-icon-btn--assign" title="Assign agent" onClick={() => setAssignTarget(p)}><FiUserPlus size={13} /></button>
                      <button className="pn-icon-btn pn-icon-btn--danger" title="Release number" onClick={() => setDeleteTarget(p)}><FiTrash2 size={13} /></button>
                    </span>
                  </div>
                ))
              )}
            </div>
            )}
          </motion.div>
        )}

        {/* ══ BUY PHONE NUMBERS ══ */}
        {subTab === "buy" && (
          <motion.div key="buy" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            {buyError && <div className="pn-toast pn-toast--err pn-toast--gap">{buyError}</div>}

            {buyLoading ? (
              <div className="pn-table">
                <div className="pn-table-head pn-table-head--buy">
                  <span>Number</span><span>City</span><span>State/Province</span><span>Capabilities</span><span>Actions</span>
                </div>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="pn-table-row">
                    <span className="pn-cell pn-cell--number"><div className="skeleton skeleton-text" style={{ width: '75%', height: 11 }} /></span>
                    <span className="pn-cell"><div className="skeleton skeleton-text" style={{ width: '60%', height: 11 }} /></span>
                    <span className="pn-cell"><div className="skeleton skeleton-text" style={{ width: '55%', height: 11 }} /></span>
                    <span className="pn-cell" style={{ display: 'flex', gap: 5 }}>
                      <div className="skeleton skeleton-rect" style={{ width: 32, height: 18, borderRadius: 4 }} />
                      <div className="skeleton skeleton-rect" style={{ width: 32, height: 18, borderRadius: 4 }} />
                    </span>
                    <span className="pn-cell">
                      <div className="skeleton skeleton-rect" style={{ width: 60, height: 26, borderRadius: 6 }} />
                    </span>
                  </div>
                ))}
              </div>
            ) : (
            <div className="pn-table">
              <div className="pn-table-head pn-table-head--buy">
                <SortHeader label="Number" sk="number" />
                <SortHeader label="City" sk="city" />
                <SortHeader label="State/Province" sk="region" />
                <span>Capabilities</span>
                <span>Actions</span>
              </div>
              {sortedAvailable.length === 0 ? (
                <div className="pn-empty">
                  <FiMapPin size={20} className="pn-empty-icon" />
                  <span>No numbers found. Try a different area code, city, or state.</span>
                </div>
              ) : (
                sortedAvailable.map((n, idx) => {
                  const num = getNum(n);
                  return (
                    <div key={num || idx} className="pn-table-row">
                      <span className="pn-cell pn-cell--number">{n.friendly_name ?? n.friendlyName ?? num}</span>
                      <span className="pn-cell">{getCity(n) || "—"}</span>
                      <span className="pn-cell pn-cell--region">{getRegion(n) || "—"}</span>
                      <span className="pn-cell pn-cell--caps">{capString(n.capabilities)}</span>
                      <span className="pn-cell">
                        <button
                          className="pn-purchase-btn"
                          onClick={() => setPurchaseTarget(n)}
                          title="Purchase this number"
                        >
                          <FiShoppingBag size={12} />
                          Purchase
                        </button>
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* ── Assign Agent Modal ── */}
      <SafeAnimatePresence>
        {assignTarget && (
          <AssignModal
            phone={assignTarget}
            agents={agents}
            onClose={() => setAssignTarget(null)}
            onConfirm={handleAssignConfirm}
          />
        )}
      </SafeAnimatePresence>

      {/* ── Purchase Modal ── */}
      <SafeAnimatePresence>
        {purchaseTarget && (
          <PurchaseModal
            number={purchaseTarget}
            onClose={() => setPurchaseTarget(null)}
            onConfirm={handlePurchaseConfirm}
          />
        )}
      </SafeAnimatePresence>

      {/* ── Delete Confirm Modal ── */}
      <SafeAnimatePresence>
        {deleteTarget && (
          <DeleteModal
            phone={deleteTarget}
            onClose={() => setDeleteTarget(null)}
            onConfirm={(twilioSid, phoneNumberId) => handleDeleteConfirm(twilioSid, phoneNumberId)}
          />
        )}
      </SafeAnimatePresence>
    </>
  );
};

export default PhoneNumbersTab;
